import React, { useState } from 'react';
import { AppLayout } from '../layouts/AppLayout';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import {
  HelpCircle, ChevronDown, BookOpen, MessageCircle, Mail,
  FileText, Shield, Brain, BarChart3, Search
} from 'lucide-react';

const FAQS = [
  {
    cat: 'Início Rápido',
    icon: BookOpen,
    items: [
      {
        q: 'Como faço meu primeiro login?',
        a: 'Use o e-mail institucional cadastrado pelo administrador do seu órgão. Caso não tenha credenciais, solicite ao gestor de Casa Civil ou CGE do seu estado.'
      },
      {
        q: 'O que é o painel "Meus Atos"?',
        a: 'É sua caixa de entrada personalizada. Mostra os atos de controle sob sua responsabilidade, organizados por urgência (vencidos, urgentes, próximos, no prazo, concluídos).'
      },
      {
        q: 'Como uso a busca global?',
        a: 'Pressione ⌘K (Mac) ou Ctrl+K (Windows/Linux) em qualquer tela. Você pode buscar atos, módulos e ações instantaneamente.'
      },
    ],
  },
  {
    cat: 'Atos de Controle',
    icon: FileText,
    items: [
      {
        q: 'Quem pode criar um Ato de Controle?',
        a: 'Por padrão: CGE, Controle Interno e Auditoria. ADMIN_ESTADUAL pode criar apenas se o Super Admin BlueGov tiver concedido a exceção via flag. Consulte o documento /docs/RBAC_ATOS_CONTROLE.md.'
      },
      {
        q: 'O que significa "SLA Vencido"?',
        a: 'É o ato cujo prazo legal já passou e ainda não foi cumprido. Aparece em vermelho no Dashboard e no painel Meus Atos.'
      },
      {
        q: 'Como anexar evidências a um ato?',
        a: 'Abra o detalhe do ato, vá na aba "Evidências" e clique em "Adicionar Evidência". Aceita PDF, DOCX, XLSX e imagens.'
      },
    ],
  },
  {
    cat: 'Compliance',
    icon: Shield,
    items: [
      {
        q: 'Como funciona o módulo LGPD?',
        a: 'Mapeia tratamentos de dados pessoais conforme Lei 13.709/2018. Cada base legal, finalidade e tempo de retenção é registrado e auditável.'
      },
      {
        q: 'Onde vejo a Transparência Ativa?',
        a: 'Menu "Compliance & Riscos" → Transparência. Indicadores e dados publicáveis seguem a Lei 12.527/2011.'
      },
      {
        q: 'O sistema atende à IN CGU 03/2017?',
        a: 'Sim. A governança, plano anual de auditoria e trilhas de evidência seguem a IN CGU 03/2017 e a metodologia COSO ERM.'
      },
    ],
  },
  {
    cat: 'Inteligência Artificial',
    icon: Brain,
    items: [
      {
        q: 'O que faz o AI Engine?',
        a: 'Classifica automaticamente atos por área temática, sugere áreas responsáveis e identifica riscos via análise semântica.'
      },
      {
        q: 'A IA toma decisões sozinha?',
        a: 'Não. A IA é assistiva. Toda decisão final é humana. As sugestões da IA ficam registradas no audit_log.'
      },
    ],
  },
  {
    cat: 'Relatórios & Indicadores',
    icon: BarChart3,
    items: [
      {
        q: 'Como exporto relatórios?',
        a: 'Menu "Relatórios" → escolha o tipo (executivo, operacional, compliance) e o formato (PDF, XLSX).'
      },
      {
        q: 'Posso comparar indicadores entre órgãos?',
        a: 'Sim, no painel "Casa Civil" (visão integrada) ou no "Admin Governo" (consolidação multi-secretaria).'
      },
    ],
  },
];

export const HelpPage = () => {
  const [search, setSearch] = useState('');
  const [openId, setOpenId] = useState(null);

  const filteredCats = FAQS.map(cat => ({
    ...cat,
    items: cat.items.filter(i =>
      i.q.toLowerCase().includes(search.toLowerCase()) ||
      i.a.toLowerCase().includes(search.toLowerCase())
    ),
  })).filter(cat => cat.items.length > 0);

  return (
    <AppLayout>
      <div className="max-w-4xl mx-auto" data-testid="help-page">
        <div className="mb-8 text-center">
          <div className="inline-flex p-3 rounded-full bg-blue-100 mb-3">
            <HelpCircle className="w-7 h-7 text-[#1d4ed8]" />
          </div>
          <h1 className="text-3xl font-bold text-slate-900">Central de Ajuda</h1>
          <p className="text-slate-500 mt-2">Tire suas dúvidas e encontre rapidamente o que precisa</p>
        </div>

        {/* Busca */}
        <div className="relative mb-8">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            data-testid="help-search-input"
            placeholder="Como criar um ato? Como exportar relatórios?..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 h-12"
          />
        </div>

        {/* FAQ por categoria */}
        <div className="space-y-6">
          {filteredCats.map(cat => (
            <div key={cat.cat} data-testid={`help-cat-${cat.cat.toLowerCase().replace(/\s/g, '-')}`}>
              <div className="flex items-center gap-2 mb-3">
                <cat.icon className="w-5 h-5 text-[#1d4ed8]" />
                <h2 className="text-lg font-semibold text-slate-900">{cat.cat}</h2>
              </div>
              <div className="space-y-2">
                {cat.items.map((item, idx) => {
                  const id = `${cat.cat}-${idx}`;
                  const open = openId === id;
                  return (
                    <Card key={id} className="overflow-hidden">
                      <button
                        onClick={() => setOpenId(open ? null : id)}
                        data-testid={`faq-q-${id.toLowerCase().replace(/\s/g, '-')}`}
                        className="w-full text-left p-4 flex items-center justify-between hover:bg-slate-50 transition-colors"
                      >
                        <span className="font-medium text-slate-900">{item.q}</span>
                        <ChevronDown className={`w-4 h-4 text-slate-500 transition-transform ${open ? 'rotate-180' : ''}`} />
                      </button>
                      {open && (
                        <CardContent className="pt-0 pb-4 text-sm text-slate-600 border-t">
                          <p className="pt-4">{item.a}</p>
                        </CardContent>
                      )}
                    </Card>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Suporte adicional */}
        <Card className="mt-10 bg-gradient-to-br from-blue-50 to-blue-50 border-blue-200">
          <CardContent className="p-6 flex flex-col md:flex-row items-center justify-between gap-4">
            <div>
              <p className="font-semibold text-slate-900">Não encontrou o que procurava?</p>
              <p className="text-sm text-slate-600">Nossa equipe de suporte está pronta para ajudar.</p>
            </div>
            <div className="flex gap-2">
              <a
                href="mailto:suporte@bluegov.ai"
                data-testid="contact-email-btn"
                className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 text-sm font-medium"
              >
                <Mail className="w-4 h-4" /> E-mail
              </a>
              <a
                href="https://bluegov.ai/suporte"
                target="_blank"
                rel="noreferrer"
                data-testid="contact-chat-btn"
                className="inline-flex items-center gap-2 px-4 py-2 bg-[#1d4ed8] text-white rounded-lg hover:bg-[#5a36d6] text-sm font-medium"
              >
                <MessageCircle className="w-4 h-4" /> Chat
              </a>
            </div>
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  );
};

export default HelpPage;
